import type { Rect, UrbanLayout } from "./streets.js";
import { CURB_W, SIDEWALK_W } from "./streetPlan.js";
import type { StreetPlan } from "./streetPlan.js";

/**
 * Where the curb is lowered (pure, unit tested): at both ends of every crosswalk (a ramp with a
 * tactile strip on the sidewalk) and where a parcel's path meets the street (a driveway: a paved
 * apron across the verge and the sidewalk, and a ramp over the gutter). The curb rects of the plan
 * are split around every cut. Ramps are wedges from the sidewalk top at the curb line down to the
 * asphalt `RAMP_DEPTH` into the road.
 */

export const RAMP_DEPTH = 0.6;
export const DRIVEWAY_W = 4.2;
export const TACTILE_D = 0.6;

export interface Ramp {
  /** Centre of the wedge's top edge on the curb line. */
  x: number;
  z: number;
  /** Axis of the road it belongs to, which side of the centre line (+1 / -1), its width along the road. */
  alongZ: boolean;
  side: 1 | -1;
  width: number;
}

export interface StreetCuts {
  ramps: Ramp[];
  aprons: Rect[];
  /** Tactile paving at the crossings (on the sidewalk, against the curb). */
  tactile: (Rect & { alongZ: boolean })[];
}

interface Cut {
  alongZ: boolean;
  /** The curb line coordinate (x for a road along Z) and the along interval removed. */
  line: number;
  a0: number;
  a1: number;
}

/** Splits curb rects around the cuts (both in place on the plan) and returns the pieces. */
function splitCurbs(plan: StreetPlan, cuts: readonly Cut[]): void {
  const out: StreetPlan["curbs"] = [];
  for (const c of plan.curbs) {
    const along0 = c.alongZ ? c.z - c.d / 2 : c.x - c.w / 2;
    const along1 = c.alongZ ? c.z + c.d / 2 : c.x + c.w / 2;
    const across = c.alongZ ? c.x : c.z;
    const mine = cuts
      .filter((k) => k.alongZ === c.alongZ && Math.abs(k.line - across) < CURB_W + 0.05)
      .filter((k) => k.a1 > along0 && k.a0 < along1)
      .sort((p, q) => p.a0 - q.a0);
    let at = along0;
    const push = (a: number, b: number) => {
      if (b - a < 0.05) return;
      out.push(
        c.alongZ
          ? { x: c.x, z: (a + b) / 2, w: c.w, d: b - a, alongZ: true }
          : { x: (a + b) / 2, z: c.z, w: b - a, d: c.d, alongZ: false },
      );
    };
    for (const k of mine) {
      push(at, Math.max(at, k.a0));
      at = Math.max(at, k.a1);
    }
    push(at, along1);
  }
  plan.curbs = out;
}

export function cutStreets(plan: StreetPlan, layout: UrbanLayout): StreetCuts {
  const cuts: Cut[] = [];
  const result: StreetCuts = { ramps: [], aprons: [], tactile: [] };
  // Crosswalk ends.
  for (const cw of plan.crosswalks) {
    const seg = plan.segments.find(
      (s) =>
        s.alongZ === cw.alongZ &&
        Math.abs(s.center - (cw.alongZ ? cw.x : cw.z)) < 0.01 &&
        (cw.alongZ ? cw.z : cw.x) >= s.from - 0.01 &&
        (cw.alongZ ? cw.z : cw.x) <= s.to + 0.01,
    );
    if (!seg) continue;
    const mid = cw.alongZ ? cw.z : cw.x;
    for (const side of [-1, 1] as const) {
      const line = seg.center + side * (seg.hw + CURB_W / 2);
      const a0 = mid - cw.depth / 2;
      const a1 = mid + cw.depth / 2;
      cuts.push({ alongZ: seg.alongZ, line, a0, a1 });
      const edge = seg.center + side * seg.hw;
      result.ramps.push(
        seg.alongZ
          ? { x: edge, z: mid, alongZ: true, side, width: cw.depth }
          : { x: mid, z: edge, alongZ: false, side, width: cw.depth },
      );
      const t = seg.center + side * (seg.hw + TACTILE_D / 2 + 0.02);
      result.tactile.push(
        seg.alongZ
          ? { x: t, z: mid, w: TACTILE_D, d: cw.depth, alongZ: true }
          : { x: mid, z: t, w: cw.depth, d: TACTILE_D, alongZ: false },
      );
    }
  }
  // Driveways: a parcel path that ends at the pad's edge facing a street.
  for (const path of layout.paths) {
    const pad = layout.pads.find(
      (p) => Math.abs(path.x - p.x) <= p.w / 2 && Math.abs(path.z - p.z) <= p.d / 2,
    );
    if (!pad) continue;
    const alongX = path.w > path.d; // the path runs along X
    // The path end on the pad's edge, and the outward direction.
    const ends: [number, number, number][] = alongX
      ? [
          [path.x - path.w / 2, path.z, -1],
          [path.x + path.w / 2, path.z, 1],
        ]
      : [
          [path.x, path.z - path.d / 2, -1],
          [path.x, path.z + path.d / 2, 1],
        ];
    const padEdge = (dir: number) =>
      alongX ? pad.x + (dir * pad.w) / 2 : pad.z + (dir * pad.d) / 2;
    const end = ends.reduce((best, e) =>
      Math.abs((alongX ? e[0] : e[1]) - padEdge(e[2])) <
      Math.abs((alongX ? best[0] : best[1]) - padEdge(best[2]))
        ? e
        : best,
    );
    const dir = end[2];
    const from = alongX ? end[0] : end[1];
    const lat = alongX ? end[1] : end[0];
    // The nearest street ahead whose curb runs across the path.
    let best: { curb: number; seg: (typeof plan.segments)[number]; side: 1 | -1 } | null = null;
    for (const s of plan.segments) {
      if (s.alongZ !== alongX) continue; // a path along X meets a street along Z
      if (lat < s.from + 2 || lat > s.to - 2) continue;
      const side: 1 | -1 = dir > 0 ? -1 : 1; // the curb that faces the parcel
      const curb = s.center + side * s.hw;
      const dist = (curb - from) * dir;
      if (dist < 0 || dist > SIDEWALK_W + 14) continue;
      if (!best || dist < (best.curb - from) * dir) best = { curb, seg: s, side };
    }
    if (!best) continue;
    const a = Math.min(from, best.curb);
    const b = Math.max(from, best.curb);
    result.aprons.push(
      alongX
        ? { x: (a + b) / 2, z: lat, w: b - a, d: DRIVEWAY_W }
        : { x: lat, z: (a + b) / 2, w: DRIVEWAY_W, d: b - a },
    );
    cuts.push({
      alongZ: best.seg.alongZ,
      line: best.curb + best.side * (CURB_W / 2),
      a0: lat - DRIVEWAY_W / 2,
      a1: lat + DRIVEWAY_W / 2,
    });
    result.ramps.push(
      best.seg.alongZ
        ? { x: best.curb, z: lat, alongZ: true, side: best.side, width: DRIVEWAY_W }
        : { x: lat, z: best.curb, alongZ: false, side: best.side, width: DRIVEWAY_W },
    );
  }
  splitCurbs(plan, cuts);
  return result;
}

/**
 * A ramp as triangles (top edge on the curb line at `top`, toe RAMP_DEPTH into the road at 0):
 * four triangles (slope and two sides; the back is hidden in the sidewalk, no bottom), each
 * with its own three corners.
 */
export function rampTriangles(r: Ramp, top: number): { positions: number[]; indices: number[] } {
  const hw = r.width / 2;
  // Local frame: u along the road, v across toward the road centre (from the curb line).
  const pts: [number, number, number][] = [
    [-hw, 0, top],
    [hw, 0, top],
    [hw, RAMP_DEPTH, 0],
    [-hw, RAMP_DEPTH, 0],
    [-hw, 0, 0],
    [hw, 0, 0],
  ];
  const positions: number[] = [];
  for (const [u, v, y] of pts) {
    const across = -r.side * v; // toward the centre
    if (r.alongZ) positions.push(r.x + across, y, r.z + u);
    else positions.push(r.x + u, y, r.z + across);
  }
  const indices = [0, 1, 2, 0, 2, 3, 0, 3, 4, 1, 5, 2];
  // Make the slope face up (winding depends on side and axis).
  const out: number[] = [];
  for (let t = 0; t < indices.length; t += 3) {
    const a = indices[t]! * 3;
    const b = indices[t + 1]! * 3;
    const c = indices[t + 2]! * 3;
    const ux = positions[b]! - positions[a]!;
    const uy = positions[b + 1]! - positions[a + 1]!;
    const uz = positions[b + 2]! - positions[a + 2]!;
    const vx = positions[c]! - positions[a]!;
    const vy = positions[c + 1]! - positions[a + 1]!;
    const vz = positions[c + 2]! - positions[a + 2]!;
    const ny = uz * vx - ux * vz;
    // Sides (vertical faces) keep any winding the double-sided collider needs; slope faces up.
    const vertical = Math.abs(ny) < 1e-6 && Math.abs(uy) + Math.abs(vy) > 0;
    if (!vertical && ny < 0) out.push(indices[t]!, indices[t + 2]!, indices[t + 1]!);
    else out.push(indices[t]!, indices[t + 1]!, indices[t + 2]!);
  }
  // Every face its own corners: the slope keeps a clean normal (not averaged with the sides).
  const flat: number[] = [];
  for (const i of out) flat.push(positions[i * 3]!, positions[i * 3 + 1]!, positions[i * 3 + 2]!);
  return { positions: flat, indices: out.map((_, i) => i) };
}
